import { ReactNode, useEffect } from "react";
import Link from "next/link";
import { usePathname, useSearchParams } from "next/navigation";
// eslint-disable-next-line no-restricted-imports -- TODO: update to `next/navigation` before using this component in App Router
import { useRouter } from "next/router";
import { useIntersectionObserver } from "usehooks-ts";

import { Action, Category } from "@/constants/events";
import { SnackBarMessages, useSnackBar } from "@/context/SnackBarContext";
import { useEventTracker } from "@/hooks/useEventTracker";
import { getMenuItemUrl } from "@/utils/menuItemUrl";
import toggleURLSearchParamValue from "@/utils/toggleURLSearchParamValue";
import { useBrandPDPQueryParams } from "@/utils/useBrandPDPQueryParams";

import AddToBagButton from "@/components/AddToBagButton";
import CardFlag from "@/components/botanic/Card/CardFlag";
import StarRating from "@/components/botanic/StarRating";
import { FilterName } from "@/components/DynamicFilters/types";
import DirectShippingIcon from "@/components/Icons/direct-shipping.svg";
import ScooterIcon from "@/components/Icons/scooter.svg";
import StorefrontIcon from "@/components/Icons/storefront.svg";
import Image from "@/components/Image";
import OfferLabel from "@/components/OfferLabel";

import { ShopItem } from "./types";

export type ShopItemCardProps = {
  shopItem: ShopItem;
};

const ShopItemCard: React.FC<ShopItemCardProps> = ({ shopItem }) => {
  const { publishEvent } = useEventTracker();
  const { ref, isIntersecting } = useIntersectionObserver({
    freezeOnceVisible: true,
  });

  const {
    amountRaw,
    brand,
    brandSlug,
    brandId,
    boostTags,
    category,
    cbdLabel,
    collapsedResultsSummary,
    dealActive,
    dealDiscountLabel,
    dispensaryName,
    dispensaryId,
    distanceMi,
    fulfillmentMethods = [],
    imageUrl,
    name,
    price,
    originalPrice,
    offerLabel,
    amount,
    productSlug,
    productId,
    reviewCount,
    reviewRating,
    strainCategory,
    strainId,
    subcategory,
    tags = [],
    thcLabel,
    dispensarySlug,
    menuItemId,
    variantId,
  } = shopItem.attributes;

  const { type } = shopItem;

  const hasDelivery = fulfillmentMethods.includes("delivery");
  const hasPickup = fulfillmentMethods.includes("pickup");
  const hasShipping = fulfillmentMethods.includes("shipping");
  const isStaffPick = !!(tags && tags[0] === "staff pick");
  const cardFlagText = tags
    ? tags[0]?.charAt(0).toUpperCase() + tags[0]?.slice(1)
    : "";

  const brandPdpQueryParams = useBrandPDPQueryParams(
    fulfillmentMethods[0],
    variantId,
    amountRaw,
  );

  const pdpUrl =
    type === "product"
      ? `/brands/${brandSlug}/products/${productSlug}?${brandPdpQueryParams}`
      : dispensarySlug && menuItemId
        ? getMenuItemUrl(
            {
              dispensarySlug: dispensarySlug,
              menuItemId: menuItemId,
              name: name,
            },
            variantId,
          )
        : "";

  const linkProps = {
    href: pdpUrl,
    onClick: () => {
      publishEvent({
        action: Action.click,
        brandId: brandId || undefined,
        category: Category.unifiedShoppingExperience,
        dispensaryId: dispensaryId || undefined,
        label:
          type === "product"
            ? "v2 product card - brand pdp"
            : "v2 product card - menu pdp",
        menuItemId: menuItemId || undefined,
        personalizationUserWeightIds: boostTags,
        productId: productId || undefined,
        strainId: strainId || undefined,
      });
    },
  };

  const pdpLinkButton = (
    <Link {...linkProps}>
      <div className="button button--secondary">see all buying options</div>
    </Link>
  );

  const cta =
    variantId && menuItemId ? (
      <CallToActionButton
        fallbackComponent={pdpLinkButton}
        itemAttributes={shopItem.attributes}
      />
    ) : (
      pdpLinkButton
    );

  const variantInfo = price ? (
    <div>
      <PriceDisplay
        dealActive={dealActive}
        dealDiscountLabel={dealDiscountLabel}
        offerLabel={offerLabel}
        originalPrice={`$${originalPrice?.toFixed(2)}`}
        price={`$${price?.toFixed(2)}`}
        amount={amount}
      />
      {dispensaryName && distanceMi ? (
        <div className="text-xs font-bold line-clamp-2 overflow-hidden text-ellipsis">
          {distanceMi.toFixed(2)} mi away at {dispensaryName}
        </div>
      ) : null}
    </div>
  ) : null;

  const collapsedResultsSummaryDisplay = collapsedResultsSummary ? (
    <div className="text-xs">{collapsedResultsSummary}</div>
  ) : null;

  const fulfillmentDisplay = (
    <div className="flex gap-xs flex-wrap">
      {hasPickup && (
        <div className="flex gap-xs items-center bg-leafly-white rounded px-sm">
          <StorefrontIcon height="12" />
          <span className="text-xs font-bold">Pickup</span>
        </div>
      )}
      {hasDelivery && (
        <div className="flex gap-xs items-center bg-leafly-white rounded px-sm">
          <ScooterIcon height="12" />
          <span className="text-xs font-bold">Delivery</span>
        </div>
      )}
      {hasShipping && (
        <div className="flex gap-xs items-center bg-green-20 rounded pr-sm pl-xs">
          <DirectShippingIcon height="12" />
          <span className="text-xs font-bold">Ships directly to you</span>
        </div>
      )}
    </div>
  );

  const categoryDisplay = subcategory ? subcategory : category;

  useEffect(() => {
    // * Only fire card impressions when the card is in browser viewport.
    if (isIntersecting) {
      publishEvent({
        action: Action.impression,
        brandId: brandId || undefined,
        category: Category.unifiedShoppingExperience,
        dispensaryId: dispensaryId || undefined,
        label:
          type === "product"
            ? "v2 product card - brand pdp"
            : "v2 product card - menu pdp",
        menuItemId: menuItemId || undefined,
        nonInteractionEvent: true,
        personalizationUserWeightIds: boostTags,
        productId: productId || undefined,
        strainId: strainId || undefined,
      });
    }
  }, [isIntersecting]);

  return (
    <div className="relative shadow-low rounded p-lg" ref={ref}>
      <div className="grid md:grid-cols-[1fr_210px] gap-lg">
        <CardFlag
          menuCardIndicators={{
            cardFlagText: cardFlagText,
            showStaffPick: isStaffPick,
          }}
          isHorizontalCard={true}
        />
        <div className="grid grid-cols-[104px_1fr] gap-md">
          <Link {...linkProps}>
            <div>
              <Image
                src={imageUrl}
                alt={name}
                sizes={[116]}
                ratio="square"
                data-testid="shop-item-card__image"
                imageClass="rounded"
              />
            </div>
          </Link>
          <div className="flex flex-col gap-md justify-start md:truncate">
            <Link {...linkProps} data-testid="shop-item-card-link">
              <div className="flex flex-col gap-sm justify-start">
                <div>
                  <div className="text-xs">{brand}</div>
                  <div className="font-bold md:truncate">{name}</div>
                </div>

                <div className="flex flex-col gap-xs">
                  <div className="flex flex-col md:flex-row gap-xs">
                    <div className="flex gap-xs">
                      {!!categoryDisplay && (
                        <div className="bg-leafly-white font-bold rounded px-sm text-xs">
                          {categoryDisplay}
                        </div>
                      )}
                      {!!strainCategory && (
                        <div className="bg-leafly-white font-bold rounded px-sm text-xs">
                          {strainCategory}
                        </div>
                      )}
                    </div>

                    {thcLabel || cbdLabel ? (
                      <div className="flex gap-xs">
                        {!!thcLabel && (
                          <div className="text-xs">{thcLabel}</div>
                        )}
                        {!!cbdLabel && (
                          <div className="text-xs uppercase">{`CBD ${cbdLabel}%`}</div>
                        )}
                      </div>
                    ) : null}
                  </div>

                  {reviewRating && reviewCount > 0 ? (
                    <StarRating
                      rating={reviewRating}
                      ratingCount={reviewCount}
                      showRating={false}
                      starWidth={18}
                      data-testid="menu-item-card__rating"
                    />
                  ) : null}
                </div>

                {/* On mobile screens, show all this info at the bottom of the card */}
                <div className="flex flex-col gap-sm md:hidden">
                  {variantInfo}
                  {fulfillmentDisplay}
                  {collapsedResultsSummaryDisplay}
                </div>
                {/* On medium or larger screens, show fulfillment options here at the column bottom, move price and other things to the next column (see below) */}
                <div className="hidden md:flex flex-col gap-sm mt-auto">
                  {fulfillmentDisplay}
                  {collapsedResultsSummaryDisplay}
                </div>
              </div>
            </Link>
            <div className="md:hidden">{cta}</div>
          </div>
        </div>
        {/* Extra column hidden on mobile devices, added on tablets and larger */}
        <div className="hidden md:flex flex-col gap-sm mt-1">
          <Link {...linkProps}>{variantInfo}</Link>
          <div className="mt-auto">{cta}</div>
        </div>
      </div>
    </div>
  );
};

const CallToActionButton = ({
  fallbackComponent,
  itemAttributes: {
    variantId,
    menuItemId,
    dispensaryName,
    isDispensaryInStore,
    dispensaryId,
    brandId,
    productId,
    strainId,
    boostTags,
  },
}: {
  fallbackComponent: ReactNode;
  itemAttributes: ShopItem["attributes"];
}) => {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const { push } = useRouter();
  const { addSnackBarItem } = useSnackBar();
  const appliedDispensaryName = searchParams?.get(
    `filter[${FilterName.DispensaryName}]`,
  );

  // * Can't render ATB without this information
  if (!variantId || !dispensaryName) {
    return <>{fallbackComponent}</>;
  }

  return (
    <AddToBagButton
      showIcon={false}
      defaultVariantId={variantId}
      onClick={(e) => {
        e.stopPropagation();
      }}
      inStoreCart={isDispensaryInStore || false}
      onReservation={() => {
        if (!appliedDispensaryName) {
          push(
            {
              pathname,
              query: toggleURLSearchParamValue(
                searchParams,
                `filter[${FilterName.DispensaryName}]`,
                dispensaryName,
              ).toString(),
            },
            undefined,
            { shallow: false },
          );
          addSnackBarItem(SnackBarMessages.resultsFilteredToDispensary);
        }
      }}
      className="w-full whitespace-nowrap self-end"
      trackingInfo={{
        brandId: brandId || undefined,
        dispensaryId: dispensaryId || undefined,
        menuItemId: menuItemId || undefined,
        personalizationUserWeightIds: boostTags,
        productId: productId || undefined,
        strainId: strainId || undefined,
      }}
    />
  );
};

const PriceDisplay = ({
  dealDiscountLabel,
  offerLabel,
  originalPrice,
  price,
  amount,
}: {
  amount?: string;
  dealActive: boolean;
  dealDiscountLabel?: string | null;
  offerLabel?: string | null;
  originalPrice: string | null;
  price: string;
}) => {
  return (
    <>
      {offerLabel && <OfferLabel label={offerLabel} />}

      {dealDiscountLabel && (
        <div>
          <span className="bg-notification rounded text-white text-xs mr-sm px-sm py-1">
            {dealDiscountLabel}
          </span>
        </div>
      )}

      <div className="flex gap-x-2 items-baseline flex-wrap">
        <div className="font-bold text-md">{price}</div>
        <div className="text-xs">{amount}</div>
        {dealDiscountLabel && originalPrice && (
          <div className="text-xs line-through">{originalPrice}</div>
        )}
      </div>
    </>
  );
};

export default ShopItemCard;
